import React from 'react';
import styled, { keyframes } from 'styled-components';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const SkillsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 6rem 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 3rem;
  text-align: center;
  position: relative;
  
  &::after {
    content: '';
    display: block;
    width: 50px;
    height: 3px;
    background-color: var(--secondary-color);
    margin: 0.5rem auto 0;
  }
`;

const SkillsIntro = styled.p`
  text-align: center;
  max-width: 700px;
  margin: 0 auto 3rem;
  font-size: 1.05rem;
  color: var(--dark-gray);
`;

const SkillCategoriesContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
`;

const SkillCategory = styled.div`
  background-color: var(--white);
  border: 1px solid var(--border-color);
  border-radius: 14px;
  padding: 1.75rem;
  transition: all 0.3s ease;
  animation: ${fadeIn} 0.5s ease-out both;
  animation-delay: ${props => props.$index * 0.08}s;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: var(--shadow-sm);
  }
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 1.25rem;
`;

const CategoryIcon = styled.div`
  font-size: 1.2rem;
  line-height: 1;
`;

const CategoryTitle = styled.h3`
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--secondary-color);
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const TagCloud = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const Tag = styled.span`
  background-color: #f1f5f9;
  color: #334155;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 500;
  border: 1px solid var(--border-color);
  transition: all 0.2s ease;

  &:hover {
    background-color: #eff6ff;
    border-color: #bfdbfe;
    color: var(--secondary-color);
  }
`;

const Skills = () => {
  const skillCategories = [
    {
      icon: '⟨/⟩',
      title: 'Languages',
      skills: ['Java', 'Python', 'JavaScript', 'SQL', 'HTML', 'CSS'],
    },
    {
      icon: '⚙',
      title: 'Frameworks & Libraries',
      skills: ['Spring Boot', 'Spring MVC', 'Spring Data JPA', 'Spring Security', 'Fast API', 'Flutter', 'JSF'],
    },
    {
      icon: '🗄',
      title: 'Databases',
      skills: ['Oracle SQL', 'PostgreSQL', 'Redis', 'MongoDB', 'Firebase'],
    },
    {
      icon: '☁',
      title: 'DevOps & Cloud',
      skills: ['Docker', 'AWS', 'Linux', 'Git', 'GitHub', 'Maven', 'CI/CD', 'Weblogic'],
    },
    {
      icon: '🔗',
      title: 'APIs & Integration',
      skills: ['REST APIs', 'SOAP', 'OAuth 2.0', 'JWT', 'RabbitMQ', 'Swagger', 'Postman'],
    },
    {
      icon: '📐',
      title: 'Architecture & Practices',
      skills: ['Microservices', 'Design Patterns', 'Event-Driven', 'Domain-Driven Design', 'Agile / Scrum'],
    },
  ];

  return (
    <SkillsContainer>
      <SectionTitle>Technical Skills</SectionTitle>
      
      <SkillsIntro>
        A broad, practical skill set built across banking, fintech, and mobile domains — from enterprise Java systems to cloud-native APIs and fullstack mobile apps.
      </SkillsIntro>
      
      <SkillCategoriesContainer>
        {skillCategories.map((category, index) => (
          <SkillCategory key={category.title} $index={index}>
            <CategoryHeader>
              <CategoryIcon>{category.icon}</CategoryIcon>
              <CategoryTitle>{category.title}</CategoryTitle>
            </CategoryHeader>
            <TagCloud>
              {category.skills.map(skill => (
                <Tag key={skill}>{skill}</Tag>
              ))}
            </TagCloud>
          </SkillCategory>
        ))}
      </SkillCategoriesContainer>
    </SkillsContainer>
  );
};

export default Skills;
